/**
 * HTTP client for the WaaGo API.
 *
 * Conventions implemented here mirror implementation/PHASE-003:
 *  - RFC 9457 Problem Details for every error
 *  - Bearer access token held in memory only (never localStorage — an XSS
 *    there is an account takeover); refresh lives in an httpOnly cookie
 *  - Single-flight refresh so parallel 401s cause exactly one refresh
 *  - Idempotency-Key on every mutating money request
 */

export type Problem = {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  traceId?: string
  errors?: { field: string; code: string }[]
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
  }
  /** Switch on `type`, never on `detail` — detail is localized prose. */
  is(type: string): boolean {
    return this.problem.type.endsWith(type)
  }
}

const BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/v1'

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export const tokenStore = {
  set(token: string | null) {
    accessToken = token
  },
  get(): string | null {
    return accessToken
  },
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const body = (await res.json()) as Partial<Problem>
    return {
      type: body.type ?? 'about:blank',
      title: body.title ?? res.statusText,
      status: body.status ?? res.status,
      ...(body.detail !== undefined ? { detail: body.detail } : {}),
      ...(body.traceId !== undefined ? { traceId: body.traceId } : {}),
      ...(body.errors !== undefined ? { errors: body.errors } : {}),
    }
  } catch {
    return { type: 'about:blank', title: res.statusText, status: res.status }
  }
}

async function refresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) {
        accessToken = null
        return false
      }
      const body = (await res.json()) as { accessToken: string }
      accessToken = body.accessToken
      return true
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Required by the API on every money-mutating endpoint. */
  idempotencyKey?: string
  /** Set false for the auth endpoints themselves. */
  auth?: boolean
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, idempotencyKey, auth = true } = opts

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'Accept-Language': 'fr' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (auth && accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
    return fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })
  }

  let res = await send()

  if (res.status === 401 && auth) {
    const ok = await refresh()
    if (ok) res = await send()
  }

  if (!res.ok) throw new ApiError(await toProblem(res))
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Cursor pagination envelope — see PHASE-003 conventions. */
export type Page<T> = { data: T[]; nextCursor: string | null; hasMore: boolean }

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
